import { compressImage } from '@/Core/Utils'

/**
 * Фото з iPhone (HEIC/HEIF) → JPEG перед будь-яким завантаженням. Сервер приймає лише
 * JPG/PNG/WEBP, а HEIC, навіть якщо його прийняти, не покажуть Chrome і Windows.
 *
 * Safari (iPhone, Mac) розбирає HEIC сам — малюємо його на canvas. Де браузер не вміє
 * (Chrome на Windows/Android з файлом, скопійованим з iPhone), підвантажуємо heic2any
 * (WASM, ~1,3 МБ) лише в цей момент, щоб не обтяжувати основний бандл.
 */

const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']

/** Розширення для атрибута accept: Windows не відносить .heic до image/*, без них файл не видно у виборі */
export const HEIC_ACCEPT = '.heic,.heif'

/** Найбільша сторона після конвертації: як у compressImage, більше для завантажень не треба */
const MAX_DIMENSION = 1920

const JPEG_QUALITY = 0.9

export class HeicConversionError extends Error {
    constructor(fileName: string) {
        super(`«${fileName}»: не вдалося перетворити фото HEIC. Збережіть його як JPEG і спробуйте ще раз.`)
        this.name = 'HeicConversionError'
    }
}

/** HEIC за типом або (коли браузер типу не знає — Windows) за розширенням */
export const isHeic = (file: { type: string; name: string }): boolean =>
    HEIC_TYPES.includes(file.type.toLowerCase()) || /\.(heic|heif)$/i.test(file.name)

const jpegName = (name: string): string => `${name.replace(/\.[^./]+$/, '') || 'photo'}.jpg`

/** Вбудоване декодування (Safari); null — браузер HEIC не розбирає */
const nativeToJpeg = async (file: File): Promise<Blob | null> => {
    let bitmap: ImageBitmap
    try {
        bitmap = await window.createImageBitmap(file)
    } catch {
        return null
    }
    try {
        const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height))
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(bitmap.width * scale)
        canvas.height = Math.round(bitmap.height * scale)
        const ctx = canvas.getContext('2d')
        if (!ctx) return null
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
        return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
    } finally {
        bitmap.close()
    }
}

/** Через heic2any: для браузерів без підтримки HEIC */
const libraryToJpeg = async (file: File): Promise<Blob | null> => {
    try {
        const { default: heic2any } = await import('heic2any')
        const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: JPEG_QUALITY })
        // Live/серійне фото дає кілька кадрів — беремо перший
        return Array.isArray(result) ? (result[0] ?? null) : result
    } catch {
        return null
    }
}

/** HEIC → JPEG; інший файл повертається без змін. Не вдалося — HeicConversionError */
export const heicToJpeg = async (file: File): Promise<File> => {
    if (!isHeic(file)) return file

    const blob = (await nativeToJpeg(file)) ?? (await libraryToJpeg(file))
    if (!blob) throw new HeicConversionError(file.name)

    return new File([blob], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified })
}

/**
 * Підготовка фото до завантаження: HEIC → JPEG, далі стиснення під ліміт сервера.
 * Помилку конвертації (HeicConversionError) показує викликач.
 */
export const preparePhoto = async (file: File): Promise<File> => compressImage(await heicToJpeg(file))
